/**
 * AP-39 10.2 — **Antwort des Lieferanten am Beleg.** Das Angebot hängt an der Preisanfrage,
 * die Auftragsbestätigung an der Bestellung; Kalkulation und Projekt lesen von dort (PE-19).
 * Spezifikation: docs/Arbeitspakete/AP-39/AP-39_Posteingang.md, 10. und 11.
 */
import type { DocumentKind } from './documents'

export const DOCUMENT_RESPONSE_KINDS = ['OFFER', 'CONFIRMATION'] as const
export type DocumentResponseKind = (typeof DOCUMENT_RESPONSE_KINDS)[number]

/** Welche Belegart welche Antwort trägt — jede andere hat keine. */
export const DOCUMENT_RESPONSE_OF: Partial<Record<DocumentKind, DocumentResponseKind>> = {
  INQUIRY: 'OFFER',
  PURCHASE_ORDER: 'CONFIRMATION',
}

/** 11.3 — Stand einer Auftragsbestätigung; `null` = nichts bestätigt. */
export const DOCUMENT_CONFIRMATION_STATES = ['CONFIRMED', 'CONFIRMED_DEVIATION', 'PARTIAL'] as const
export type DocumentConfirmationState = (typeof DOCUMENT_CONFIRMATION_STATES)[number]

export const DOCUMENT_CONFIRMATION_STATE_LABELS: Readonly<Record<DocumentConfirmationState, string>> = {
  CONFIRMED: 'bestätigt',
  CONFIRMED_DEVIATION: 'bestätigt mit Abweichung',
  PARTIAL: 'teilweise bestätigt',
}

/** 11.2 — eine Abweichung der Bestätigung von der Bestellung. Der Text nennt nie einen Betrag. */
export interface DocumentConfirmationDeviation {
  kind: 'QUANTITY' | 'PRICE' | 'DATE' | 'MISSING' | 'EXTRA'
  text: string
  /** nur `DATE`: um wie viele Tage später */
  days?: number
  /** nur `PRICE`: höher als bestellt (AP-35 N17-10 — die Marke an der Stücklistenzeile liest es, nicht den Text) */
  higher?: boolean
}

/** Nettopreis je Einheit einer Antwort: EP ÷ Preiseinheit × (1 − Rabatt). */
export function antwortNetto(unitPrice: number, priceUnit: number, discountPercent: number | null | undefined): number {
  return (unitPrice / Math.max(1, priceUnit || 1)) * (1 - (discountPercent ?? 0) / 100)
}

/**
 * Ein Vorschlag, wie er gelesen wurde (KI) — steht kursiv an der Zeile, bis jemand bestätigt
 * (2.2 Satz 4). `unitCode` ist die gelesene Einheit, `answerUnitCode` die Einheit, in der die
 * Antwort gelten soll, wenn sie von der Belegzeile abweicht (AP-37 O 7.4a).
 */
export interface DocumentResponseSuggestion {
  unitPrice?: number | null
  priceUnit?: number
  discountPercent?: number | null
  leadTimeDays?: number | null
  notOffered?: boolean
  alternative?: string | null
  unitCode?: string | null
  answerUnitCode?: string | null
  /** gelesene Einheit, die nicht übernommen werden konnte (Kilopreis ohne Gewicht) */
  unitHint?: string | null
  /** Auftragsbestätigung: bestätigte Menge und Termin (JJJJ-MM-TT) */
  quantity?: number | null
  deliveryAt?: string | null
  offerText?: string | null
  articleNumber?: string | null
  page?: number | null
  quote?: string | null
  quoteFound?: boolean | null
  confidence?: 'hoch' | 'mittel' | 'niedrig'
}

/** Vorgeschlagener Kopf einer Antwort. */
export interface DocumentResponseHeadSuggestion {
  /** Angebots- bzw. AB-Nummer des Lieferanten */
  supplierOfferNumber?: string | null
  offerDate?: string | null
  validUntil?: string | null
  fileName?: string | null
  model?: string | null
}

/** Die Antwort an einer Belegzeile bzw. eine zusätzliche Zeile. */
export interface DocumentLineResponseView {
  id: string
  lineId: string | null
  label: string | null
  articleNumber: string | null
  /** nur mit `correspondence.price.view` */
  unitPrice?: number | null
  priceUnit: number
  /** nur mit `correspondence.price.view` */
  discountPercent?: number | null
  /** nur mit `correspondence.price.view` — EP ÷ PE × (1 − Rabatt), vier Stellen */
  netPrice?: number | null
  unitCode: string | null
  quantity: number | null
  deliveryAt: string | null
  leadTimeDays: number | null
  notOffered: boolean
  alternative: string | null
  note: string | null
  /** nur mit `correspondence.price.view` */
  suggested?: DocumentResponseSuggestion | null
  confirmedAt: string | null
  updatedAt: string
}

/** Eine Belegzeile mit ihrer Antwort. */
export interface DocumentResponseLineView {
  lineId: string
  positionLabel: string
  articleNumber: string | null
  name: string
  description: string | null
  quantity: number
  unitCode: string | null
  /** Bestellung: der bestellte Preis, gegen den die Bestätigung abgeglichen wird (nur mit Preisrecht) */
  orderUnitPrice?: number | null
  orderPriceUnit?: number
  orderDiscountPercent?: number | null
  /** gewünschter Termin: der Zeile, sonst des Belegs, sonst der Stücklistenzeile (JJJJ-MM-TT) */
  orderDeliveryDate?: string | null
  /** Bestellung: Abweichungen der Bestätigung (11.2); ohne Bestätigung „nicht bestätigt" */
  deviations?: DocumentConfirmationDeviation[]
  response: DocumentLineResponseView | null
}

export interface DocumentResponseHead {
  id: string
  externalNumber: string | null
  externalDate: string | null
  validUntil: string | null
  note: string | null
  /** nur mit `correspondence.price.view` */
  suggested?: DocumentResponseHeadSuggestion | null
  createdAt: string
  updatedAt: string
}

/** `GET /documents/:id/responses/:kind` — die geltende Antwort samt früheren. */
export interface DocumentResponseView {
  documentId: string
  kind: DocumentResponseKind
  /** Antworten lassen sich erfassen (nicht storniert, nicht vorläufig) */
  writable: boolean
  current: DocumentResponseHead | null
  lines: DocumentResponseLineView[]
  /** zusätzliche Zeilen ohne Belegzeile */
  extra: DocumentLineResponseView[]
  linesTotal: number
  linesAnswered: number
  /** Bestellung: Stand der Bestätigung (11.3) mit Zahl und Kurztext der Abweichungen */
  state?: DocumentConfirmationState | null
  deviationCount?: number
  deviationText?: string | null
  /** nur mit `correspondence.price.view` — Summe der Antwort netto */
  total?: number | null
  earlier: { id: string; externalNumber: string | null; externalDate: string | null; validUntil: string | null; createdAt: string; linesAnswered: number }[]
}
